/**
 * Session Guardian — Advanced real-time session protection system.
 * Monitors for unauthorized access, code manipulation, session hijacking,
 * memory tampering, timing attacks, and other threats that could compromise user data.
 */

import { safeSetItem } from '@/lib/safeStorage';

const GUARDIAN_SETTINGS_KEY = 'aphenis_session_guardian';
const GUARDIAN_ALERTS_KEY = 'aphenis_guardian_alerts';
const GUARDIAN_FINGERPRINT_KEY = 'aphenis_guardian_fp';
const GUARDIAN_FINGERPRINT_VERSION_KEY = 'aphenis_guardian_fp_v';
const GUARDIAN_INTEGRITY_KEY = 'aphenis_guardian_integrity';
const GUARDIAN_CANARY_KEY = 'aphenis_guardian_canary';
const GUARDIAN_SESSION_START_KEY = 'aphenis_guardian_session_start';
const GUARDIAN_HEARTBEAT_KEY = 'aphenis_guardian_heartbeat';
const GUARDIAN_FINGERPRINT_VERSION = 'v2';

export type ThreatLevel = 'info' | 'warning' | 'critical';
export type ThreatCategory = 'session' | 'integrity' | 'network' | 'injection' | 'device' | 'timing' | 'memory';

export interface GuardianAlert {
  id: string;
  timestamp: number;
  category: ThreatCategory;
  level: ThreatLevel;
  title: string;
  description: string;
  dismissed: boolean;
  mitigationApplied?: string;
}

export interface GuardianSettings {
  enabled: boolean;
  monitorDomManipulation: boolean;
  monitorSessionHijacking: boolean;
  monitorDevTools: boolean;
  monitorNetworkAnomalies: boolean;
  monitorCodeIntegrity: boolean;
  monitorMemoryCanary: boolean;
  monitorTimingAttacks: boolean;
  monitorCspViolations: boolean;
  monitorXhrIntercept: boolean;
  monitorStorageIntegrity: boolean;
  autoLockOnCritical: boolean;
  notifyOnWarning: boolean;
  wipeOnRepeatedCritical: boolean;
  criticalThresholdForWipe: number;
  lockdownMode: boolean; // When true, immediately locks on ANY threat
}

const DEFAULT_SETTINGS: GuardianSettings = {
  enabled: true,
  monitorDomManipulation: true,
  monitorSessionHijacking: true,
  monitorDevTools: true,
  monitorNetworkAnomalies: true,
  monitorCodeIntegrity: true,
  monitorMemoryCanary: true,
  monitorTimingAttacks: true,
  monitorCspViolations: true,
  monitorXhrIntercept: true,
  monitorStorageIntegrity: true,
  autoLockOnCritical: true,
  notifyOnWarning: true,
  wipeOnRepeatedCritical: false,
  criticalThresholdForWipe: 5,
  lockdownMode: false,
};

export function loadGuardianSettings(): GuardianSettings {
  try {
    const stored = localStorage.getItem(GUARDIAN_SETTINGS_KEY);
    if (stored) return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return { ...DEFAULT_SETTINGS };
}

export function saveGuardianSettings(settings: GuardianSettings): void {
  try { localStorage.setItem(GUARDIAN_SETTINGS_KEY, JSON.stringify(settings)); } catch { /* quota */ }
}

export function loadGuardianAlerts(): GuardianAlert[] {
  try {
    const stored = localStorage.getItem(GUARDIAN_ALERTS_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return [];
}

function saveGuardianAlerts(alerts: GuardianAlert[]): void {
  try { localStorage.setItem(GUARDIAN_ALERTS_KEY, JSON.stringify(alerts.slice(-50))); } catch { /* quota */ }
}

export function dismissAlert(alertId: string): void {
  const alerts = loadGuardianAlerts();
  const updated = alerts.map(a => a.id === alertId ? { ...a, dismissed: true } : a);
  saveGuardianAlerts(updated);
}

export function clearAllAlerts(): void {
  localStorage.removeItem(GUARDIAN_ALERTS_KEY);
}

function addAlert(alert: Omit<GuardianAlert, 'id' | 'timestamp' | 'dismissed'>): GuardianAlert {
  const newAlert: GuardianAlert = {
    ...alert,
    id: crypto.randomUUID?.() || Math.random().toString(36).slice(2),
    timestamp: Date.now(),
    dismissed: false,
  };
  const alerts = loadGuardianAlerts();
  // Dedup: don't add same title within 30s
  const recent = alerts.find(a => a.title === alert.title && Date.now() - a.timestamp < 30000);
  if (recent) return recent;
  alerts.push(newAlert);
  saveGuardianAlerts(alerts);
  return newAlert;
}

/**
 * Generate a stable browser environment fingerprint for session binding.
 * IMPORTANT: Only include truly immutable properties — screen dimensions,
 * canvas output, and WebGL renderer can change on Android (orientation,
 * keyboard, split-screen, GPU context loss) causing false-positive alerts.
 */
function getStableEnvironmentFingerprintParts(): string[] {
  return [
    navigator.userAgent,
    navigator.language,
    navigator.languages?.join(',') || '',
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    navigator.platform || '',
  ];
}

function generateEnvironmentFingerprint(): string {
  const parts = getStableEnvironmentFingerprintParts();
  let hash = 0;
  const str = parts.join('|');
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  return hash.toString(36);
}

function hasTrustedFingerprintBaseline(): boolean {
  return localStorage.getItem(GUARDIAN_FINGERPRINT_VERSION_KEY) === GUARDIAN_FINGERPRINT_VERSION;
}

function persistEnvironmentFingerprint(fingerprint: string): void {
  safeSetItem(GUARDIAN_FINGERPRINT_KEY, fingerprint);
  safeSetItem(GUARDIAN_FINGERPRINT_VERSION_KEY, GUARDIAN_FINGERPRINT_VERSION);
}

/**
 * Generate an HMAC-like integrity hash for critical localStorage keys.
 */
function computeStorageIntegrity(): string {
  const criticalKeys = [
    'vaultmsg_uid', 'vaultmsg_encrypted_keys', 'aphenis_unlocked',
    'aphenis_local_verification', 'aphenis_privacy_settings', 'aphenis_session_guardian',
  ];
  const values = criticalKeys.map(k => {
    const v = localStorage.getItem(k);
    return k + ':' + (v ? v.length.toString() : 'null');
  }).join('|');
  let hash = 0x9e3779b9;
  for (let i = 0; i < values.length; i++) {
    hash = ((hash << 5) + hash) ^ values.charCodeAt(i);
    hash |= 0;
  }
  return hash.toString(36);
}

/** Check if guardian is in migration grace period (60s after migration). */
function isInMigrationGrace(): boolean {
  try {
    const ts = localStorage.getItem('aphenis_guardian_migration_grace');
    if (!ts) return false;
    return Date.now() - parseInt(ts) < 60000;
  } catch { return false; }
}

/**
 * Memory canary — a hidden value in a WeakRef that should never change.
 * If it does, someone is manipulating the runtime memory.
 */
let _memoryCanary: { value: string; check: () => boolean } | null = null;

function initMemoryCanary(): { value: string; check: () => boolean } {
  const secret = crypto.randomUUID?.() || Math.random().toString(36).slice(2, 18);
  const frozen = Object.freeze({ _canary: secret });
  return {
    value: secret,
    check: () => frozen._canary === secret,
  };
}

type AlertCallback = (alert: GuardianAlert) => void;
type LockCallback = () => void;
type WipeCallback = () => void;

let _destroyFn: (() => void) | null = null;

/**
 * Compute a session health score (0-100) based on current state.
 */
export function computeSessionHealth(settings: GuardianSettings): {
  score: number;
  factors: { label: string; ok: boolean; weight: number }[];
} {
  if (!settings.enabled) return { score: 0, factors: [] };

  const factors: { label: string; ok: boolean; weight: number }[] = [];
  
  // Check fingerprint consistency
  const fpOk = (() => {
    const stored = localStorage.getItem(GUARDIAN_FINGERPRINT_KEY);
    if (!hasTrustedFingerprintBaseline()) return true;
    if (!stored) return true;
    return stored === generateEnvironmentFingerprint();
  })();
  factors.push({ label: 'Environment fingerprint', ok: fpOk, weight: 25 });

  // Check storage integrity
  const intOk = (() => {
    const stored = localStorage.getItem(GUARDIAN_INTEGRITY_KEY);
    if (!stored) return true;
    return stored === computeStorageIntegrity();
  })();
  factors.push({ label: 'Storage integrity', ok: intOk, weight: 20 });

  // Check memory canary
  const canaryOk = _memoryCanary ? _memoryCanary.check() : true;
  factors.push({ label: 'Memory canary', ok: canaryOk, weight: 15 });

  // Check no recent critical alerts
  const alerts = loadGuardianAlerts();
  const recentCritical = alerts.filter(a => a.level === 'critical' && !a.dismissed && Date.now() - a.timestamp < 300000);
  factors.push({ label: 'No active critical threats', ok: recentCritical.length === 0, weight: 20 });

  // Check session heartbeat freshness
  const heartbeat = localStorage.getItem(GUARDIAN_HEARTBEAT_KEY);
  const heartbeatOk = heartbeat ? (Date.now() - parseInt(heartbeat)) < 60000 : true;
  factors.push({ label: 'Session heartbeat', ok: heartbeatOk, weight: 10 });

  // Monitor coverage
  const monitorCount = getActiveMonitorCount(settings);
  const maxMonitors = 10;
  factors.push({ label: `Monitor coverage (${monitorCount}/${maxMonitors})`, ok: monitorCount >= 7, weight: 10 });

  const score = factors.reduce((sum, f) => sum + (f.ok ? f.weight : 0), 0);
  return { score, factors };
}

/**
 * Start the Session Guardian monitoring loop.
 * Returns a destroy function to clean up all listeners.
 */
export function startSessionGuardian(
  settings: GuardianSettings,
  onAlert: AlertCallback,
  onCriticalLock: LockCallback,
  onEmergencyWipe?: WipeCallback
): () => void {
  _destroyFn?.();

  if (!settings.enabled) {
    _destroyFn = null;
    return () => {};
  }

  // Master try/catch — if setup itself throws, clean up gracefully
  try {

  const cleanups: (() => void)[] = [];
  const pendingTimeouts = new Set<number>();

  const scheduleTimeout = (callback: () => void, delay: number) => {
    const timeoutId = window.setTimeout(() => {
      pendingTimeouts.delete(timeoutId);
      callback();
    }, delay);
    pendingTimeouts.add(timeoutId);
    return timeoutId;
  };
  cleanups.push(() => {
    pendingTimeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
    pendingTimeouts.clear();
  });

  const validateEnvironmentFingerprint = (
    onConfirmedMismatch: (currentFingerprint: string) => void,
    options?: { rebaselineOnTransient?: boolean }
  ) => {
    const storedFp = localStorage.getItem(GUARDIAN_FINGERPRINT_KEY);
    const firstSample = generateEnvironmentFingerprint();

    if (!storedFp || !hasTrustedFingerprintBaseline()) {
      persistEnvironmentFingerprint(firstSample);
      return;
    }

    if (storedFp === firstSample) {
      persistEnvironmentFingerprint(firstSample);
      return;
    }

    scheduleTimeout(() => {
      if (document.hidden) return;

      const trustedStoredFp = localStorage.getItem(GUARDIAN_FINGERPRINT_KEY);
      const secondSample = generateEnvironmentFingerprint();

      if (!trustedStoredFp || !hasTrustedFingerprintBaseline()) {
        persistEnvironmentFingerprint(secondSample);
        return;
      }

      if (trustedStoredFp === secondSample) {
        return;
      }

      if (secondSample !== firstSample) {
        if (options?.rebaselineOnTransient) {
          persistEnvironmentFingerprint(secondSample);
        }
        return;
      }

      onConfirmedMismatch(secondSample);
    }, 1800);
  };

  // Track critical alert count for wipe threshold
  let criticalCount = 0;
  const handleCriticalEscalation = () => {
    criticalCount++;
    if (settings.lockdownMode || settings.autoLockOnCritical) {
      onCriticalLock();
    }
    if (settings.wipeOnRepeatedCritical && criticalCount >= settings.criticalThresholdForWipe && onEmergencyWipe) {
      onEmergencyWipe();
    }
  };

  // Record session start
  if (!localStorage.getItem(GUARDIAN_SESSION_START_KEY)) {
    try { localStorage.setItem(GUARDIAN_SESSION_START_KEY, Date.now().toString()); } catch { /* quota */ }
  }

  // ─── Session heartbeat (initial write — periodic updates via unified tick below) ───
  try { localStorage.setItem(GUARDIAN_HEARTBEAT_KEY, Date.now().toString()); } catch { /* quota */ }

  // ─── 1. Session Hijacking Detection (Enhanced) ───
  if (settings.monitorSessionHijacking) {
    validateEnvironmentFingerprint((currentFingerprint) => {
      const alert = addAlert({
        category: 'session',
        level: 'critical',
        title: 'Environment change detected',
        description: 'Your session appears to be running in a different browser or device environment. This could indicate session data was copied or stolen.',
        mitigationApplied: 'Session auto-locked',
      });
      persistEnvironmentFingerprint(currentFingerprint);
      onAlert(alert);
      handleCriticalEscalation();
    });

    // Monitor storage events (another tab tampering)
    const handleStorage = (e: StorageEvent) => {
      if (e.key?.startsWith('aphenis_') && e.oldValue && !e.newValue) {
        const alert = addAlert({
          category: 'session',
          level: 'warning',
          title: 'Session data cleared externally',
          description: `Key "${e.key}" was deleted from another tab or script. This may indicate unauthorized access.`,
        });
        onAlert(alert);
        if (settings.lockdownMode) handleCriticalEscalation();
      }
      if (e.key === 'aphenis_identity' && e.oldValue && e.newValue && e.oldValue !== e.newValue) {
        const alert = addAlert({
          category: 'session',
          level: 'critical',
          title: 'Identity keys modified',
          description: 'Your cryptographic identity was changed from another context. This could be a session hijacking attempt.',
          mitigationApplied: 'Session auto-locked',
        });
        onAlert(alert);
        handleCriticalEscalation();
      }
    };
    window.addEventListener('storage', handleStorage);
    cleanups.push(() => window.removeEventListener('storage', handleStorage));

    // Concurrent session detection via BroadcastChannel
    try {
      const channel = new BroadcastChannel('aphenis_guardian_sync');
      const sessionId = crypto.randomUUID?.() || Math.random().toString(36).slice(2);
      
      channel.onmessage = (e) => {
        if (e.data?.type === 'session_ping' && e.data?.sessionId !== sessionId) {
          const alert = addAlert({
            category: 'session',
            level: 'warning',
            title: 'Concurrent session detected',
            description: 'Another browser tab or window is running Aphenis simultaneously. Data could be accessed from multiple contexts.',
          });
          onAlert(alert);
          if (settings.lockdownMode) handleCriticalEscalation();
        }
      };
      
      // Announce presence
      channel.postMessage({ type: 'session_ping', sessionId });
      const pingInterval = setInterval(() => {
        channel.postMessage({ type: 'session_ping', sessionId });
      }, 30000);
      
      cleanups.push(() => { clearInterval(pingInterval); channel.close(); });
    } catch { /* BroadcastChannel not supported */ }
  }

  // ─── 2. DOM / Code Injection Monitoring (Enhanced) ───
  if (settings.monitorDomManipulation) {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of Array.from(mutation.addedNodes)) {
          if (node instanceof HTMLScriptElement && node.src && !node.src.includes(window.location.origin)) {
            const alert = addAlert({
              category: 'injection',
              level: 'critical',
              title: 'Foreign script injected',
              description: `An external script was injected from: ${(() => { try { return new URL(node.src).hostname; } catch { return node.src.slice(0, 50); } })()}. This is a strong indicator of a man-in-the-browser attack.`,
              mitigationApplied: 'Script blocked, session locked',
            });
            // Try to remove the injected script
            try { node.remove(); } catch { /* can't remove */ }
            onAlert(alert);
            handleCriticalEscalation();
          }
          if (node instanceof HTMLIFrameElement && node.src && !node.src.startsWith('about:') && !node.src.startsWith('blob:')) {
            const alert = addAlert({
              category: 'injection',
              level: 'warning',
              title: 'Suspicious iframe detected',
              description: `An iframe was added pointing to: ${node.src.slice(0, 80)}. This could be a clickjacking or phishing attempt.`,
            });
            onAlert(alert);
            if (settings.lockdownMode) handleCriticalEscalation();
          }
          // Detect inline event handler injection on existing elements
          if (node instanceof HTMLElement && node.hasAttribute('onload') && !node.closest('#root')) {
            const alert = addAlert({
              category: 'injection',
              level: 'warning',
              title: 'Inline event handler injected',
              description: `An element with an inline onload handler was injected outside the app root. This may indicate XSS.`,
            });
            onAlert(alert);
          }
        }
      }
    });
    observer.observe(document.head, { 
      childList: true, 
      subtree: true,
    });
    cleanups.push(() => observer.disconnect());
  }

  // ═══════════════════════════════════════════════════════════════════
  // Consolidated checks — instead of 6+ separate setInterval timers,
  // all periodic checks run from a single 10s "tick" to reduce CPU
  // wake-ups and memory pressure on mobile devices.
  // ═══════════════════════════════════════════════════════════════════

  // ─── 3. Code Integrity check (runs every tick ~10s, was 10s) ───
  let nativeFnSignatures: Record<string, string> | null = null;
  if (settings.monitorCodeIntegrity) {
    nativeFnSignatures = {
      'JSON.parse': JSON.parse.toString().slice(0, 30),
      'JSON.stringify': JSON.stringify.toString().slice(0, 30),
      'localStorage.getItem': localStorage.getItem.toString().slice(0, 30),
      'localStorage.setItem': localStorage.setItem.toString().slice(0, 30),
    };
  }

  const checkCodeIntegrity = () => {
    if (!settings.monitorCodeIntegrity || !nativeFnSignatures) return;
    const objProto = Object.getOwnPropertyNames(Object.prototype);
    const knownProps = new Set(['constructor', 'hasOwnProperty', 'isPrototypeOf', 'propertyIsEnumerable', 'toString', 'valueOf', 'toLocaleString', '__defineGetter__', '__defineSetter__', '__lookupGetter__', '__lookupSetter__', '__proto__']);
    const suspiciousProps = objProto.filter(p => !knownProps.has(p));
    if (suspiciousProps.length > 0) {
      const a = addAlert({ category: 'integrity', level: 'critical', title: 'Prototype pollution detected', description: `Suspicious properties on Object.prototype: ${suspiciousProps.slice(0, 5).join(', ')}.`, mitigationApplied: 'Session locked' });
      onAlert(a); handleCriticalEscalation();
    }
    if (typeof window.crypto?.getRandomValues !== 'function') {
      const a = addAlert({ category: 'integrity', level: 'critical', title: 'Crypto API tampered', description: 'The browser Crypto API has been modified or removed.', mitigationApplied: 'Session locked' });
      onAlert(a); handleCriticalEscalation();
    }
    const tamperedFns: string[] = [];
    if (JSON.parse.toString().slice(0, 30) !== nativeFnSignatures['JSON.parse']) tamperedFns.push('JSON.parse');
    if (JSON.stringify.toString().slice(0, 30) !== nativeFnSignatures['JSON.stringify']) tamperedFns.push('JSON.stringify');
    if (localStorage.getItem.toString().slice(0, 30) !== nativeFnSignatures['localStorage.getItem']) tamperedFns.push('localStorage.getItem');
    if (localStorage.setItem.toString().slice(0, 30) !== nativeFnSignatures['localStorage.setItem']) tamperedFns.push('localStorage.setItem');
    if (tamperedFns.length > 0) {
      const a = addAlert({ category: 'integrity', level: 'critical', title: 'Native functions tampered', description: `Monkey-patched: ${tamperedFns.join(', ')}.`, mitigationApplied: 'Session locked' });
      onAlert(a); handleCriticalEscalation();
    }
    try {
      if (window.eval.toString().indexOf('native code') === -1) {
        const a = addAlert({ category: 'integrity', level: 'warning', title: 'eval() function modified', description: 'The eval() function has been replaced.' });
        onAlert(a);
      }
    } catch { /* eval blocked by CSP */ }
  };

  // ─── 4. DevTools Detection (runs every 3rd tick ~30s, was 3s — less aggressive) ───
  let devToolsOpen = false;
  const checkDevTools = () => {
    if (!settings.monitorDevTools) return;
    const threshold = 160;
    const widthThreshold = window.outerWidth - window.innerWidth > threshold;
    const heightThreshold = window.outerHeight - window.innerHeight > threshold;
    const isOpen = widthThreshold || heightThreshold;
    if (isOpen && !devToolsOpen) {
      devToolsOpen = true;
      const a = addAlert({ category: 'device', level: settings.lockdownMode ? 'critical' : 'warning', title: 'Developer tools opened', description: 'Browser developer tools were detected.' });
      onAlert(a);
      if (settings.lockdownMode) handleCriticalEscalation();
    } else if (!isOpen) {
      devToolsOpen = false;
    }
  };

  // ─── 7. Memory Canary (runs every tick ~10s, was 8s) ───
  let canaryValue: string | null = null;
  if (settings.monitorMemoryCanary) {
    _memoryCanary = initMemoryCanary();
    canaryValue = _memoryCanary.value;
    try { localStorage.setItem(GUARDIAN_CANARY_KEY, canaryValue); } catch { /* quota */ }
  }
  const checkMemoryCanary = () => {
    if (!settings.monitorMemoryCanary || !canaryValue) return;
    if (isInMigrationGrace()) return;
    if (_memoryCanary && !_memoryCanary.check()) {
      const a = addAlert({ category: 'memory', level: 'critical', title: 'Memory canary violated', description: 'A frozen sentinel in memory was modified.', mitigationApplied: 'Session locked' });
      onAlert(a); handleCriticalEscalation();
    }
    const storedCanary = localStorage.getItem(GUARDIAN_CANARY_KEY);
    if (storedCanary && storedCanary !== canaryValue) {
      const a = addAlert({ category: 'memory', level: 'critical', title: 'Persisted canary tampered', description: 'The guardian canary value in storage was changed externally.', mitigationApplied: 'Session locked' });
      onAlert(a); handleCriticalEscalation();
    }
  };

  // ─── 8. Timing Attack Detection (runs every tick ~10s, was 12s) ───
  const perfNowStr = settings.monitorTimingAttacks ? performance.now.toString() : '';
  const checkTiming = () => {
    if (!settings.monitorTimingAttacks) return;
    if (performance.now.toString() !== perfNowStr) {
      const a = addAlert({ category: 'timing', level: 'critical', title: 'performance.now() tampered', description: 'The high-resolution timer has been modified.', mitigationApplied: 'Session locked' });
      onAlert(a); handleCriticalEscalation();
    }
    const now = Date.now();
    const lastHeartbeat = parseInt(localStorage.getItem(GUARDIAN_HEARTBEAT_KEY) || '0');
    if (lastHeartbeat > 0) {
      const drift = Math.abs(now - lastHeartbeat);
      if (drift > 120000) {
        const a = addAlert({ category: 'timing', level: 'warning', title: 'System clock anomaly', description: `Detected ${Math.round(drift / 1000)}s gap between heartbeats.` });
        onAlert(a);
      }
    }
  };

  // ─── 10. Storage Integrity (runs every 3rd tick ~30s, was 30s) ───
  if (settings.monitorStorageIntegrity && !isInMigrationGrace()) {
    const currentIntegrity = computeStorageIntegrity();
    const storedIntegrity = localStorage.getItem(GUARDIAN_INTEGRITY_KEY);
    if (storedIntegrity && storedIntegrity !== currentIntegrity) {
      const a = addAlert({ category: 'integrity', level: 'warning', title: 'Storage integrity mismatch', description: 'Critical localStorage values have changed since last session.' });
      onAlert(a);
    }
    try { safeSetItem(GUARDIAN_INTEGRITY_KEY, currentIntegrity); } catch { /* quota */ }
  }
  const checkStorageIntegrity = () => {
    if (!settings.monitorStorageIntegrity || isInMigrationGrace()) return;
    try { safeSetItem(GUARDIAN_INTEGRITY_KEY, computeStorageIntegrity()); } catch { /* quota */ }
  };

  // ─── Unified Tick (single setInterval replacing 6 separate ones) ───
  let tickCount = 0;
  const TICK_INTERVAL_MS = 10000;
  const unifiedTick = setInterval(() => {
    tickCount++;
    // Heartbeat: every 3rd tick (~30s)
    if (tickCount % 3 === 0) {
      try { safeSetItem(GUARDIAN_HEARTBEAT_KEY, Date.now().toString()); } catch { /* quota */ }
    }
    // Each check isolated in its own try/catch so one failure doesn't skip the rest
    try { checkCodeIntegrity(); } catch (e) { console.warn('[Guardian] codeIntegrity tick error:', e); }
    try { checkMemoryCanary(); } catch (e) { console.warn('[Guardian] memoryCanary tick error:', e); }
    try { checkTiming(); } catch (e) { console.warn('[Guardian] timing tick error:', e); }
    if (tickCount % 3 === 0) {
      try { checkDevTools(); } catch (e) { console.warn('[Guardian] devTools tick error:', e); }
    }
    if (tickCount % 3 === 0) {
      try { checkStorageIntegrity(); } catch (e) { console.warn('[Guardian] storageIntegrity tick error:', e); }
    }
  }, TICK_INTERVAL_MS);
  cleanups.push(() => {
    clearInterval(unifiedTick);
    if (settings.monitorMemoryCanary) _memoryCanary = null;
  });

  // ─── 11. Visibility-based session validation ───
  const handleVisibility = () => {
    if (!document.hidden) {
      validateEnvironmentFingerprint((currentFingerprint) => {
        const alert = addAlert({
          category: 'session',
          level: 'critical',
          title: 'Session integrity check failed',
          description: 'Environment changed while app was in background. Your session may have been cloned.',
          mitigationApplied: 'Session locked',
        });
        persistEnvironmentFingerprint(currentFingerprint);
        onAlert(alert);
        handleCriticalEscalation();
      }, { rebaselineOnTransient: true });

      // Re-check storage integrity on resume
      if (settings.monitorStorageIntegrity) {
        const currentInteg = computeStorageIntegrity();
        const storedInteg = localStorage.getItem(GUARDIAN_INTEGRITY_KEY);
        if (storedInteg && storedInteg !== currentInteg) {
          const alert = addAlert({
            category: 'integrity',
            level: 'warning',
            title: 'Storage modified while backgrounded',
            description: 'Critical data changed while the app was in background. Another application or tab may have accessed your data.',
          });
          onAlert(alert);
        }
      }
    }
  };
  document.addEventListener('visibilitychange', handleVisibility);
  cleanups.push(() => document.removeEventListener('visibilitychange', handleVisibility));

  // ─── 12. Error monitoring for injection signals ───
  const handleError = (e: ErrorEvent) => {
    if (e.filename && !e.filename.includes(window.location.origin) && e.filename.startsWith('http')) {
      const alert = addAlert({
        category: 'injection',
        level: 'warning',
        title: 'Error from foreign script',
        description: `A JavaScript error originated from an external source: ${e.filename.slice(0, 80)}. Foreign code is executing in this page.`,
      });
      onAlert(alert);
    }
  };
  window.addEventListener('error', handleError);
  cleanups.push(() => window.removeEventListener('error', handleError));

  const destroy = () => {
    cleanups.forEach(fn => fn());
    cleanups.length = 0;
    _destroyFn = null;
  };

  _destroyFn = destroy;
  return destroy;

  } catch (e) {
    // Setup crashed — clean up gracefully
    console.error('[SessionGuardian] Setup failed:', e);
    _destroyFn = null;
    return () => {};
  }
}

export function isGuardianEnabled(): boolean {
  return loadGuardianSettings().enabled;
}

export function getActiveMonitorCount(settings: GuardianSettings): number {
  if (!settings.enabled) return 0;
  return [
    settings.monitorDomManipulation,
    settings.monitorSessionHijacking,
    settings.monitorDevTools,
    settings.monitorNetworkAnomalies,
    settings.monitorCodeIntegrity,
    settings.monitorMemoryCanary,
    settings.monitorTimingAttacks,
    settings.monitorCspViolations,
    settings.monitorXhrIntercept,
    settings.monitorStorageIntegrity,
  ].filter(Boolean).length;
}

/**
 * Get the session uptime in milliseconds.
 */
export function getSessionUptime(): number {
  const start = localStorage.getItem(GUARDIAN_SESSION_START_KEY);
  if (!start) return 0;
  return Date.now() - parseInt(start);
}
